import type { MeContext } from '@vizit/api';
import { ROLE, SCOPE_TYPE } from '../constants';
import { hasGrant } from './grants';
import { isOrgAdmin } from './is-org-admin';

// Client-side gating is not a security boundary — the BE enforces access on every call.

/**
 * Whether the user may write to one workspace's assets — favoriting, for
 * instance.
 *
 * Deliberately stricter than {@link canAccessWorkspace}: that one honours the
 * legacy portfolio mount, so a grant on a *sibling* workspace under the same
 * portfolio is enough to open a PDP. A write addressed at a single asset row
 * is not covered by that grant, and the backend rejects it. Mirroring the
 * backend's own check here is what lets the UI disable the control rather than
 * offer an action that 404s.
 *
 * Takes only a workspace id: the owning portfolio is resolved from the
 * context's hierarchy so that callers — feature code keyed on `workspace_id` —
 * never have to carry a portfolio id to ask the question.
 */
export function canWriteWorkspaceAsset(context: MeContext, workspaceId?: string | null): boolean {
  if (isOrgAdmin(context)) {
    return true;
  }

  if (!workspaceId) {
    return false;
  }

  if (hasGrant(context, ROLE.WorkspaceUser, SCOPE_TYPE.Workspace, workspaceId)) {
    return true;
  }

  const owningPortfolio = context.organization.portfolios.find((portfolio) =>
    portfolio.workspaces?.some((workspace) => workspace.id === workspaceId),
  );

  return !!owningPortfolio && hasGrant(context, ROLE.PortfolioAdmin, SCOPE_TYPE.Portfolio, owningPortfolio.id);
}
